import { ChevronLeft, ChevronRight, Home } from '@mui/icons-material';
import { CarryOutOutlined } from '@ant-design/icons';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  ClickAwayListener,
  Divider,
  List,
  ListItemButton,
  Paper,
  Popper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import MainCard from 'components/MainCard';
import { dateOptionsShort } from 'constants/dateFormats';
import useErrorSnackbar from 'hooks/useErrorSnackbar';
import { useMarkEventTaskFinalized, useMyEventTaskIndex } from 'hooks/useEventTaskIndex';
import useSuccessSnackbar from 'hooks/useSuccessSnackbar';
import { MouseEvent as ReactMouseEvent, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { dispatch } from 'store';
import { setCurrentEventId } from 'store/reducers/menu';
import { ThemeMode } from 'types/config';
import { EventTaskIndexItem } from 'types/eventTaskIndex';

const PAGE_SIZE = 20;
const ENABLE_PERSONAL_TASKS_POPPER = false;

interface PersonalTasksProps {
  tasksPath?: string;
  eventTaskPath?: string | null;
  prominent?: boolean;
}

const formatDate = (date: Date | null) =>
  date ? date.toLocaleDateString([], dateOptionsShort) : 'No date';

const dueColor = (date: Date | null) => {
  if (!date) return 'text.secondary';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(date);
  due.setHours(0, 0, 0, 0);
  return due < today ? 'error.main' : 'text.primary';
};

const eventMeta = (task: EventTaskIndexItem) =>
  [formatDate(task.dateStart), task.clTypeofEvent, task.entTypeName, task.venueName]
    .filter(Boolean)
    .join(' · ');

const PersonalTasks = ({
  tasksPath = '/tasks',
  eventTaskPath = '/event/tasks',
  prominent = false
}: PersonalTasksProps) => {
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const { items, loading, error, pageIndex, hasNext, hasPrev, nextPage, prevPage } =
    useMyEventTaskIndex(true, PAGE_SIZE);
  const markFinalized = useMarkEventTaskFinalized();
  const successSnackbar = useSuccessSnackbar();
  const errorSnackbar = useErrorSnackbar();
  const anchorRef = useRef<any>(null);
  const [open, setOpen] = useState(false);
  const [finalizingId, setFinalizingId] = useState<string | null>(null);

  const handleOpenTasksPage = () => {
    setOpen(false);
    navigate(tasksPath);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleOpenEvent = (eventId: string) => {
    if (!eventTaskPath) return;
    dispatch(setCurrentEventId(eventId));
    navigate(eventTaskPath);
    setOpen(false);
  };

  const handleMarkFinalized = async (event: ReactMouseEvent, task: EventTaskIndexItem) => {
    event.stopPropagation();
    setFinalizingId(task.id);
    try {
      await markFinalized(task);
      successSnackbar('Task finalized');
    } catch (err) {
      errorSnackbar(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setFinalizingId(null);
    }
  };

  const iconBackColorOpen = theme.palette.mode === ThemeMode.DARK ? 'grey.200' : 'grey.300';
  const iconBackColor = theme.palette.mode === ThemeMode.DARK ? 'background.default' : 'grey.100';
  const badgeContent = items.length > 0 ? (hasNext ? `${PAGE_SIZE}+` : items.length) : 0;
  const showHomeShortcut = prominent && location.pathname !== '/';

  return (
    <>
      {showHomeShortcut && (
        <Box sx={{ flexShrink: 0 }}>
          <Tooltip title="Home">
            <IconButton
              color="secondary"
              variant="light"
              aria-label="Home"
              onClick={() => navigate('/')}
              sx={{
                color: 'text.primary',
                bgcolor: iconBackColor
              }}
            >
              <Home />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Box sx={{ flexShrink: 0, ml: prominent ? 0 : 0.75 }}>
        <Tooltip title="Tasks">
          <IconButton
            color="secondary"
            variant="light"
            sx={{
              color: 'text.primary',
              bgcolor: ENABLE_PERSONAL_TASKS_POPPER && open ? iconBackColorOpen : iconBackColor,
              px: prominent ? 1 : undefined,
              gap: prominent ? 0.75 : undefined
            }}
            aria-label="Tasks"
            ref={anchorRef}
            aria-controls={ENABLE_PERSONAL_TASKS_POPPER && open ? 'tasks-grow' : undefined}
            aria-haspopup={ENABLE_PERSONAL_TASKS_POPPER ? 'true' : undefined}
            onClick={ENABLE_PERSONAL_TASKS_POPPER ? handleToggle : handleOpenTasksPage}
          >
            <Badge badgeContent={badgeContent} color="primary">
              <CarryOutOutlined />
            </Badge>
            {prominent && <Typography variant="body2" sx={{ fontWeight: 600 }}>My Tasks</Typography>}
          </IconButton>
        </Tooltip>
      </Box>

      {ENABLE_PERSONAL_TASKS_POPPER && (
        <Popper
          placement={matchesXs ? 'bottom' : 'bottom-end'}
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
          popperOptions={{
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [matchesXs ? -5 : 0, 9]
                }
              }
            ]
          }}
        >
          {({ TransitionProps }) => (
            <Transitions
              type="grow"
              position={matchesXs ? 'top' : 'top-right'}
              sx={{ overflow: 'hidden' }}
              in={open}
              {...TransitionProps}
            >
              <Paper
                sx={{
                  boxShadow: theme.customShadows.z1,
                  width: '100%',
                  minWidth: 460,
                  maxWidth: 560,
                  [theme.breakpoints.down('md')]: {
                    minWidth: 300,
                    maxWidth: 320
                  }
                }}
              >
                <ClickAwayListener onClickAway={handleClose}>
                  <MainCard elevation={0} border={false} content={false}>
                    <Stack
                      direction="row"
                      sx={{ alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25 }}
                    >
                      <Typography variant="subtitle1">My Tasks</Typography>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        {loading && <CircularProgress size={18} />}
                        <Button size="extraSmall" onClick={handleOpenTasksPage}>
                          View All
                        </Button>
                      </Stack>
                    </Stack>
                    <Divider />
                    <List component="nav" sx={{ maxHeight: '70vh', overflow: 'auto', p: 0 }}>
                      {items.map((task) => (
                        <ListItemButton
                          key={task.id}
                          onClick={() => handleOpenEvent(task.eventId)}
                          sx={{
                            alignItems: 'stretch',
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            py: 1.25
                          }}
                        >
                          <Stack spacing={0.75} sx={{ minWidth: 0, width: '100%' }}>
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="subtitle2" sx={{ lineHeight: 1.25 }}>
                                  {task.title}
                                </Typography>
                                {task.valueLabel && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                    {task.valueLabel}
                                  </Typography>
                                )}
                                {task.note && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 0.25, whiteSpace: 'pre-line' }}
                                  >
                                    {task.note}
                                  </Typography>
                                )}
                              </Box>
                              <Button
                                variant="outlined"
                                size="extraSmall"
                                disabled={finalizingId === task.id}
                                onClick={(event) => handleMarkFinalized(event, task)}
                                sx={{ flexShrink: 0 }}
                              >
                                {finalizingId === task.id ? 'Saving' : 'Mark Finalized'}
                              </Button>
                            </Stack>
                            <Stack spacing={0.25}>
                              <Typography
                                variant="caption"
                                sx={{ color: dueColor(task.dueDate), fontWeight: 600 }}
                              >
                                Due {formatDate(task.dueDate)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {task.eventDisplayTitle} · {eventMeta(task)}
                              </Typography>
                            </Stack>
                          </Stack>
                        </ListItemButton>
                      ))}
                      {!loading && !error && items.length === 0 && (
                        <ListItemButton sx={{ justifyContent: 'center', py: 2 }}>
                          <Typography>No tasks at the moment</Typography>
                        </ListItemButton>
                      )}
                      {error && (
                        <ListItemButton sx={{ justifyContent: 'center', py: 2 }}>
                          <Stack spacing={0.25} sx={{ alignItems: 'center' }}>
                            <Typography color="error">Unable to load tasks</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {error.message}
                            </Typography>
                          </Stack>
                        </ListItemButton>
                      )}
                    </List>
                    <Divider />
                    <Stack
                      direction="row"
                      sx={{ alignItems: 'center', justifyContent: 'space-between', px: 1.25, py: 1 }}
                    >
                      <Button
                        size="extraSmall"
                        startIcon={<ChevronLeft />}
                        disabled={!hasPrev || loading}
                        onClick={prevPage}
                      >
                        Prev
                      </Button>
                      <Typography variant="caption" color="text.secondary">
                        Page {pageIndex + 1}
                      </Typography>
                      <Button
                        size="extraSmall"
                        endIcon={<ChevronRight />}
                        disabled={!hasNext || loading}
                        onClick={nextPage}
                      >
                        Next
                      </Button>
                    </Stack>
                  </MainCard>
                </ClickAwayListener>
              </Paper>
            </Transitions>
          )}
        </Popper>
      )}
    </>
  );
};

export default PersonalTasks;